import { common } from '@kurtosys/ksys-app-template';
import { AuthenticationMode, IEndpointConfiguration } from '@kurtosys/types/api/common/index.js';
import { TEndpoints } from '@kurtosys/types/api/endpoints/index.js';
import { IRequestOptions } from '@kurtosys/types/api/IRequestOptions.js';

// import { wrap } from 'comlink';
// import { TApiClientWorkerApi } from '../workers/api-client-worker.js';
import { CachableRequest } from '../workers/requests/CachableRequest.js';

import { BaseKsysRequest } from './BaseKsysRequest.js';
import { MockDataRequest } from './MockDataRequest.js';

// export const apiClientWorker = wrap<TApiClientWorkerApi>(
// 	new Worker(new URL('../workers/api-client-worker.js', import.meta.url)),
// );

const DEFAULT_AUTH_MODE = AuthenticationMode.header;
export abstract class KsysRequest<TRequestBody, TResponseBody> extends BaseKsysRequest<TRequestBody, TResponseBody> {
	abstract getExplicitEndpoint: () => string;
	abstract get endpointConfiguration(): IEndpointConfiguration;
	getEndpointConfiguration<K extends keyof TEndpoints>(
		pillar: K,
		endpoint: keyof TEndpoints[K],
	): IEndpointConfiguration {
		if (
			this.endpointsConfiguration &&
			this.endpointsConfiguration.endpoints &&
			this.endpointsConfiguration.endpoints[pillar]
		) {
			return this.endpointsConfiguration.endpoints[pillar][endpoint];
		}
		return {
			authenticationMode: DEFAULT_AUTH_MODE,
		};
	}
	getEndpoint(overrideOptions?: Partial<IRequestOptions<TRequestBody>>): string {
		const options: IRequestOptions<TRequestBody> = common.commonUtils.deepMergeObjects(
			this.options,
			overrideOptions,
		);
		const { endpoint, urlParams, queryString, placeholders } = options;
		let response = endpoint || this.getExplicitEndpoint();
		if (response && placeholders) {
			response = common.commonUtils.replacePlaceholders({ values: placeholders, text: response });
		}
		if (response && urlParams) {
			const join = response.endsWith('/') ? '' : '/';
			const cleanUrlParams = urlParams.map((urlParam) => encodeURIComponent(urlParam));
			response = `${response}${join}${cleanUrlParams.join('/')}`;
		}
		if (response && queryString) {
			const join = response.includes('?') ? '&' : '?';
			const queryStringPieces = Object.keys(queryString).reduce((acc, key) => {
				const value = queryString[key];
				acc.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
				return acc;
			}, []);
			response = `${response}${join}${queryStringPieces.join('&')}`;
		}
		return response;
	}
	getUrl = (overrideOptions?: Partial<IRequestOptions<TRequestBody>>): string => {
		const options: IRequestOptions<TRequestBody> = common.commonUtils.deepMergeObjects(
			this.options,
			overrideOptions,
		);
		const { baseUrl, explicitUrl } = options;
		if (explicitUrl) {
			return explicitUrl;
		}
		const endpoint = this.getEndpoint(overrideOptions);

		const separator = '/';
		const rawPieces = [baseUrl, endpoint];
		const pieces = rawPieces.map((piece, index) => {
			let response = piece;
			if (index !== 0 && response.startsWith(separator)) {
				response = response.substr(separator.length);
			}
			if (index !== rawPieces.length - 1 && response.endsWith(separator)) {
				response = response.substr(0, response.length - separator.length);
			}
			return response;
		});
		let fullUrl = pieces.join(separator);
		// Check if GET
		if (options.method === 'GET') {
			if (options.disableCache) {
				fullUrl += `&_dc=${Date.now()}`;
			}
		}
		return fullUrl;
	};

	execute = async (overrideOptions?: Partial<IRequestOptions<TRequestBody>>): Promise<TResponseBody> => {
		let response;
		const options: IRequestOptions<TRequestBody> = common.commonUtils.deepMergeObjects(
			this.options,
			overrideOptions,
		);
		if (options && options.mockData && options.mockData.enabled) {
			const mockDataRequest = new MockDataRequest(this.getUrl(overrideOptions), options);
			response = mockDataRequest.fetch();
		} else {
			// With worker
			// const apiResponse = await this.callApi(overrideOptions);
			// response = apiResponse.responseBody;
			// Without worker: Leaving this here for reference
			// This code was moved to the worker
			const callApiResponse = await this.callApi(overrideOptions);
			response = await this.handleResponse(callApiResponse, overrideOptions);
		}
		if (this.orchestrateResponse) {
			response = this.orchestrateResponse(response);
		}
		return response;
	};
	callApi = async (overrideOptions?: Partial<IRequestOptions<TRequestBody>>): Promise<Response> => {
		const options: IRequestOptions<TRequestBody> = common.commonUtils.deepMergeObjects(
			this.options,
			overrideOptions,
		);
		const explicitMethod = this.getExplicitMethod ? this.getExplicitMethod() : 'POST';
		const { method = explicitMethod, headers = {}, body = {}, authenticationMode = DEFAULT_AUTH_MODE } = options;
		const tokenKey = 'X-KSYS-TOKEN';
		if (authenticationMode === AuthenticationMode.header && this.token && this.token.value && !headers[tokenKey]) {
			headers[tokenKey] = this.token.value;
		}
		if (options.debug) {
			headers['X-KSYS-DEBUG'] = 'true';
		}
		if (options.fetchUserCountry) {
			if (options.debug) {
				console.info('Fetching user country code...');
			}
			headers['X-KSYS-COUNTRY'] = '1';
		}
		if (options.disableCache) {
			headers['cache-control'] = 'no-cache';
			headers['pragma'] = 'no-cache';
		}
		const request: RequestInit = {
			method,
			credentials: 'same-origin',
			headers: {
				// eslint-disable-next-line @typescript-eslint/naming-convention
				Accept: 'application/json',
				// eslint-disable-next-line @typescript-eslint/naming-convention
				'Content-Type': 'application/json',
				...headers,
			},
			body: JSON.stringify(body),
			signal: options.signal,
		};

		if (method.toUpperCase() === 'GET') {
			delete request.body;
		}

		try {
			const url = this.getUrl(overrideOptions);
			// With worker
			// return await apiClientWorker.fetch(this.options, overrideOptions, url, request);
			// Without worker: Leaving this here for reference
			// This code was moved to the worker
			if (options.disableCachableRequests) {
				if (options.debug) {
					console.info('cache disabled, fetching response');
				}
				return await fetch(url, request);
			}
			const cachableRequest = new CachableRequest(url, request, options.debug);
			return await cachableRequest.fetch();
		} catch (error) {
			console.warn('request error', error);
		}
	};
	handleResponse = async (
		response: Response,
		overrideOptions?: Partial<IRequestOptions<TRequestBody>>,
	): Promise<any> => {
		const options = common.commonUtils.deepMergeObjects(this.options, overrideOptions);
		// Get the response content type so we can decide how to handle the response
		const rawContentType = response.headers.get('content-type') || '';
		// content type can have a list of attributes attached to split by ";", the first attribute would the content type.
		const rawContentTypeList = rawContentType.split(';');
		const contentType = rawContentTypeList.length > 0 ? rawContentTypeList[0] : '';

		// extract user country onto the window object, as we do not expose response headers via the API Client.
		if (options.fetchUserCountry && response.headers.has('X-KSYS-COUNTRY')) {
			const countryCode = response.headers.get('X-KSYS-COUNTRY');
			if (options.debug) {
				console.info('User Country Code', countryCode);
			}
			if (window) {
				window[common.constants.GLOBAL_USER_COUNTRY_KEY] = countryCode;
			}
		}

		// Set a default response type
		let responseType;

		if (options.debug) {
			console.log('response content type', contentType);
			if (response.headers.has('X-KSYS-DEBUG')) {
				console.info('cloudflare debug', response.headers.get('X-KSYS-DEBUG'));
			}
		}

		/**
		 * Change response type for other content types
		 *
		 * Common Mime type reference https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
		 */
		switch (contentType) {
			case '':
				responseType = null;
				break;
			case 'application/json':
				responseType = 'json';
				break;
			case 'text/plain':
			case 'text/html':
				responseType = 'text';
				break;
			case 'text/csv':
			case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
			case 'application/msword':
			case 'application/vnd.ms-powerpoint':
			case 'application/vnd.ms-excel':
			case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
			case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
			case 'application/pdf':
			case 'application/zip':
			case 'image/png':
			case 'image/jpeg':
			case 'image/gif':
			case 'image/svg+xml':
			case 'data:image/png':
			case 'data:image/jpeg':
			case 'data:image/gif':
			case 'data:image/svg+xml':
				responseType = 'blob';
				break;
			default:
				responseType = 'buffer';
		}

		// Return a blank if the response is a 204
		if (response.status === 204) {
			return '';
		}

		let responseBody;
		try {
			// Await the response according to response type, e.g. response.json(), response.text()
			// Check if response[responseType] is a function before executing it
			responseBody =
				responseType && typeof response[responseType] === 'function'
					? await response[responseType]()
					: response;
		} catch (e) {
			throw Error(`Failed to parse response for ${this.getUrl()}`);
		}
		// Throw an exception for responses in the 400 range
		if (response.status >= 400 && response.status < 500) {
			throw Error(`${response.status}:\n${JSON.stringify(responseBody)}`);
		}

		// Throw an error for responses not in the 200 range
		if (!response.ok) {
			throw Error(response.statusText);
		}

		// If everything is ok return the response body
		return responseBody;
	};
}
